import React from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';

interface InsufficientTokensModalProps {
  show: boolean;
  onHide: () => void;
  buttonCost: number;
}

/**
 * Modal that appears when a user tries to click a paid button without sufficient tokens
 * 
 * Features:
 * - Displays the cost of the button
 * - Provides a direct link to the settings page to buy more tokens
 * - Uses bootstrap modal for consistent UX
 */
const InsufficientTokensModal: React.FC<InsufficientTokensModalProps> = ({
  show,
  onHide,
  buttonCost
}) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useUser();
  const navigate = useNavigate();

  const handleLoginClick = () => {
    onHide();
    // Navigate to login page and store current path for redirect back
    navigate('/login', { state: { from: { pathname: window.location.pathname } } });
  };

  const handleSignupClick = () => {
    onHide();
    // Navigate to signup page and store current path for redirect back
    navigate('/signup', { state: { from: { pathname: window.location.pathname } } });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-coin me-2 text-warning"></i>
          {isLoggedIn 
            ? t('buttons.insufficientTokens', 'Insufficient Tokens')
            : t('auth.loginRequired', 'Login Required')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center mb-4">
          <div className="display-1 text-warning mb-3">
            <i className={`bi ${isLoggedIn ? 'bi-coin' : 'bi-person-lock'}`}></i>
          </div>
          {isLoggedIn ? (
            <>
              <p className="lead">
                {t('buttons.insufficientTokensMessage', 'You need more tokens to click this button.')}
              </p>
              <p>
                {t('buttons.requiredTokens', 'This button requires {{cost}} tokens per click.', { cost: buttonCost })}
              </p>
            </>
          ) : (
            <>
              <p className="lead">
                {t('auth.loginRequiredForPaidButtons', 'You need to log in to click paid buttons.')}
              </p>
              <p>
                {t('auth.createAccountForTokens', 'Create an account or log in to purchase and use tokens.')}
              </p>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-center">
        <button 
          className="btn btn-secondary me-2" 
          onClick={onHide}
        >
          {t('common.cancel', 'Cancel')}
        </button>
        
        {isLoggedIn ? (
          <Link 
            to="/wallet" 
            className="btn btn-primary"
            onClick={onHide}
          >
            <i className="bi bi-wallet2 me-1"></i>
            {t('buttons.buyTokens', 'Buy Tokens')}
          </Link>
        ) : (
          <>
            <button 
              className="btn btn-primary me-2"
              onClick={handleLoginClick}
            >
              <i className="bi bi-box-arrow-in-right me-1"></i>
              {t('auth.login', 'Login')}
            </button>
            <button 
              className="btn btn-success"
              onClick={handleSignupClick}
            >
              <i className="bi bi-person-plus me-1"></i>
              {t('auth.signup', 'Sign Up')}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default InsufficientTokensModal; 